<template>
  <div :class="`user-card ${noActions ? 'no-actions' : ''}`">
    <div class="user-card__content" :data-test-id="`user-card-${user.id}`" @click="onClick">
      <UserInfo :user="user" :sub-info="shownSubInfoRef" />
      <Badge v-if="badgePropsRef" :variant="badgePropsRef.variant">
        {{ t(badgePropsRef.textKey) }}
      </Badge>
      <div v-if="slots.actions && !noActions" class="user-card__actions">
        <slot name="actions"></slot>
      </div>
      <div
        v-if="!noActions"
        class="user-card__more-options"
        data-action="user-card"
        :data-attached="JSON.stringify(user)"
      >
        <MoreOptionsIcon data-test-id="more-options-group" color="black" />
      </div>
    </div>
    <div
      v-if="
        matchingExplanationRef &&
        !isMatchingExplanationPendingRef &&
        checkPermissionsGroup(programRolesRef, PermissionsGroupsEnumType.ADMIN) &&
        mentorDetails &&
        userIdRef === matchingExplanationRef?.mentor?.menteeId
      "
      class="user-card__matching-explanation"
    >
      <ExtendableInformations :title="t('admin.groups.groupStructureModal.matching.title')" :content="mentorDetails" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, useSlots } from 'vue'

import useMatchingExplanation from '@/features/admin/groups/hooks/use-matching-explanation.hook'
import { PermissionsGroupsEnumType } from '@/features/auth/types'
import type { SessionStatusEnumType } from '@/features/sessions/types'
import { getUserSessionStatusBadgeProps } from '@/features/sessions/utils'
import ExtendableInformations from '@/features/ui/components/extendable-informations/extendable-informations.vue'
import MoreOptionsIcon from '@/features/ui/components/icons/more-options-icon.vue'
import Badge from '@/components/Badge.vue'
import type { UserProfileModalType } from '@/features/ui/store/modal/modal.store'
import modalsStore from '@/features/ui/store/modal/modal.store'
import UserInfo from '@/features/users/components/user-info/user-info.vue'
import useProgramRoles from '@/features/users/hooks/use-program-roles.hook'
import type { V2UserDataType } from '@/features/users/types'
import { userRolesNamesMap } from '@/features/users/utils'
import checkPermissionsGroup from '@/features/users/utils'
import { useI18n } from '@/lib/i18n'

const { t, locale } = useI18n()
const slots = useSlots() as ReturnType<typeof useSlots>

const badgePropsRef = computed(() => {
  if (!props.status) return null
  return getUserSessionStatusBadgeProps(props.status)
})

const props = defineProps<{
  user: V2UserDataType
  status?: SessionStatusEnumType
  subInfo?: string
  noActions?: boolean
  overrideOnClick?: boolean
  groupId?: string
}>()
const userIdRef = ref(props.user.id)

const groupIdRef = computed(() => props.groupId ?? null)

const shownSubInfoRef = computed(() =>
  props.subInfo ? props.subInfo : `${props.user.roles.map((role) => t(userRolesNamesMap[role])).join(', ')}`,
)
const { programRolesRef } = useProgramRoles()
const { matchingExplanationRef, isPendingRef: isMatchingExplanationPendingRef } = useMatchingExplanation(
  userIdRef,
  groupIdRef,
)

const mentorDetails = computed(() => {
  if (matchingExplanationRef.value?.mentor.detailsFr && matchingExplanationRef.value?.mentor.detailsEn) {
    return locale.value === 'fr'
      ? matchingExplanationRef.value?.mentor.detailsFr
      : matchingExplanationRef.value?.mentor.detailsEn
  } else if (matchingExplanationRef.value?.mentor.detailsFr && !matchingExplanationRef.value?.mentor.detailsEn) {
    return matchingExplanationRef.value?.mentor.detailsFr
  }
  return ''
})

const emit = defineEmits(['emit:click'])

function onClick(e: Event) {
  // guard to not open the modal when clicking on the more options icon
  if ((e.target as HTMLElement).closest('[data-action="user-card"]') || props.noActions) {
    return
  }

  if (props.overrideOnClick) {
    emit('emit:click', props.user)
  } else {
    openUserProfile()
  }
}

function openUserProfile() {
  modalsStore.toggleModal('user-profile', true)
  modalsStore.updateModalAttachedData<UserProfileModalType>('user-profile', { userId: props.user.id })
}
</script>

<style lang="scss">
@import './user-card.scss';
</style>
